import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Users, Plus, Pencil, Trash2, Shield, Upload, TrendingUp, FileText,
  X, Check, AlertTriangle, Clock, KeyRound, Briefcase, Send, RefreshCw, Copy
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ROLE_META = {
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800 border-red-300', icon: Shield },
  org_admin: { label: 'Org Admin', color: 'bg-indigo-100 text-indigo-800 border-indigo-300', icon: Shield },
  executive: { label: 'Executive (CPO / Head)', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Briefcase },
  category_manager: { label: 'Category Manager', color: 'bg-teal-100 text-teal-800 border-teal-300', icon: TrendingUp },
  data_uploader: { label: 'Data Uploader', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Upload },
  data_analytics: { label: 'Data Analytics', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: TrendingUp },
  report_user: { label: 'Report User', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: FileText },
};

const PAGE_LABELS = {
  '/': 'Dashboard', '/spend-analysis': 'Spend Analysis', '/spend-distribution': 'Spend Distribution',
  '/category-console': 'Category Console', '/commonality-analysis': 'Commonality',
  '/supplier-directory': 'Supplier Directory', '/suppliers': 'Supplier Management',
  '/comparative-analysis': 'Comparative Analysis',
  '/p2p-analytics': 'P2P Analytics', '/data-quality': 'Data Quality',
};

const AI_CAPABILITY_LABELS = {
  ai_page_insights: { label: 'Page & Report Insights', desc: 'Contextual AI insights on analytics pages and PDF reports' },
  ai_dashboard_digest: { label: 'Dashboard / Monday Digest', desc: 'AI-generated executive digest on the dashboard' },
  ai_recommendations: { label: 'AI Recommendations', desc: 'CPO-level filter-aware recommendations across pages' },
  ai_action_plans: { label: 'Action Plans', desc: 'Detailed per-recommendation action plans' },
  ai_category_strategy: { label: 'Category Strategy', desc: 'Long-form category strategy briefs' },
};
const AI_CAPABILITY_KEYS = Object.keys(AI_CAPABILITY_LABELS);

const defaultCapabilities = (enabled = true) =>
  AI_CAPABILITY_KEYS.reduce((acc, k) => ({ ...acc, [k]: enabled }), {});

/**
 * Generate a strong random password matching the server-side validator
 * (min length, at least one uppercase, lowercase, digit, and special char).
 * 14 chars gives ~90 bits of entropy from this alphabet — safely above bank-grade.
 */
const generateStrongPassword = (length = 14) => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';        // no I/O to avoid l-look-alikes
  const lower = 'abcdefghijkmnopqrstuvwxyz';        // no l
  const digits = '23456789';                        // no 0/1
  const symbols = '!@#$%^&*-_=+?';
  const all = upper + lower + digits + symbols;
  const rand = (pool) => pool[Math.floor((crypto.getRandomValues(new Uint32Array(1))[0] / 0xFFFFFFFF) * pool.length)];
  // Guarantee one of each class
  let out = [rand(upper), rand(lower), rand(digits), rand(symbols)];
  while (out.length < length) out.push(rand(all));
  // Fisher-Yates shuffle with crypto
  for (let i = out.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join('');
};

const INCOMPATIBLE = [
  new Set(['data_uploader', 'data_analytics']),
  new Set(['data_uploader', 'report_user']),
  new Set(['data_uploader', 'executive']),
];

function checkSoD(roles) {
  for (const pair of INCOMPATIBLE) {
    if ([...pair].every(r => roles.includes(r))) {
      return `Cannot combine ${[...pair].map(r => ROLE_META[r]?.label).join(' and ')}`;
    }
  }
  return null;
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [seats, setSeats] = useState(null);
  const [rolesConfig, setRolesConfig] = useState(null);

  // Read own roles from localStorage so the UI can adapt for Org Admins vs Super Admins
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isSuperAdmin = (me.roles || []).includes('super_admin');
  const isOrgAdmin = !isSuperAdmin && (me.roles || []).includes('org_admin');

  // Form state
  const [form, setForm] = useState({
    email: '', password: '', full_name: '', organization_id: '',
    roles: [], page_permissions: [], ai_access: true, action_plan_access: true,
    ai_capabilities: defaultCapabilities(true),
  });
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [inviteTarget, setInviteTarget] = useState(null);
  const [inviteNewPassword, setInviteNewPassword] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [passwordMode, setPasswordMode] = useState('auto');  // 'auto' | 'manual'
  const [showGeneratedPwd, setShowGeneratedPwd] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, orgsRes, seatsRes, cfgRes] = await Promise.all([
        api.get('/admin/users').catch(() => ({ data: [] })),
        api.get('/admin/organizations').catch(() => ({ data: [] })),
        api.get('/admin/seats').catch(() => ({ data: null })),
        api.get('/admin/roles-config').catch(() => ({ data: null })),
      ]);
      setUsers(usersRes.data);
      setOrgs(orgsRes.data);
      setSeats(seatsRes.data && !seatsRes.data.per_org ? seatsRes.data : null);
      setRolesConfig(cfgRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchAudit = async () => {
    try {
      const res = await api.get('/admin/audit-logs?limit=100');
      setAuditLogs(res.data);
      setShowAudit(true);
    } catch { toast.error('Failed to load audit logs'); }
  };

  const openCreate = () => {
    setEditUser(null);
    setPasswordMode('auto');
    setShowGeneratedPwd(true);
    setForm({
      email: '', password: generateStrongPassword(), full_name: '', organization_id: orgs[0]?.id || '',
      roles: [], page_permissions: Object.keys(PAGE_LABELS),
      ai_access: true, action_plan_access: true,
      ai_capabilities: defaultCapabilities(true),
    });
    setShowForm(true);
  };

  const regeneratePassword = () => {
    setForm(p => ({ ...p, password: generateStrongPassword() }));
    setShowGeneratedPwd(true);
  };

  const copyGeneratedPassword = async () => {
    try {
      await navigator.clipboard.writeText(form.password);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const changePasswordMode = (mode) => {
    setPasswordMode(mode);
    if (mode === 'auto') {
      setForm(p => ({ ...p, password: generateStrongPassword() }));
      setShowGeneratedPwd(true);
    } else {
      setForm(p => ({ ...p, password: '' }));
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    const uAiAccess = u.ai_access !== false;
    const uCaps = { ...defaultCapabilities(uAiAccess), ...(u.ai_capabilities || {}) };
    setForm({
      email: u.email, password: '', full_name: u.full_name, organization_id: u.organization_id,
      roles: u.roles || [], page_permissions: u.page_permissions || [],
      ai_access: uAiAccess,
      action_plan_access: u.action_plan_access !== false,
      ai_capabilities: uCaps,
    });
    setShowForm(true);
  };

  const toggleCapability = (cap) => {
    setForm(prev => ({
      ...prev,
      ai_capabilities: { ...prev.ai_capabilities, [cap]: !prev.ai_capabilities?.[cap] },
    }));
  };

  const toggleRole = (role) => {
    setForm(prev => {
      const has = prev.roles.includes(role);
      const next = has ? prev.roles.filter(r => r !== role) : [...prev.roles, role];
      return { ...prev, roles: next };
    });
  };

  const togglePage = (page) => {
    setForm(prev => {
      const has = prev.page_permissions.includes(page);
      return { ...prev, page_permissions: has ? prev.page_permissions.filter(p => p !== page) : [...prev.page_permissions, page] };
    });
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) return toast.error('Name is required');
    if (!form.roles.length) return toast.error('Select at least one role');
    const sodErr = checkSoD(form.roles);
    if (sodErr) return toast.error(sodErr);

    setSaving(true);
    try {
      // When master AI access is off, zero out all capabilities so the backend persists them consistently
      const effectiveCaps = form.ai_access ? form.ai_capabilities : defaultCapabilities(false);
      // Keep the legacy action_plan_access field in sync with the granular toggle for backward compat
      const effectiveActionPlanAccess = form.ai_access && effectiveCaps.ai_action_plans !== false;

      if (editUser) {
        await api.put(`/admin/users/${editUser.id}`, {
          full_name: form.full_name,
          roles: form.roles,
          page_permissions: form.page_permissions,
          ai_access: form.ai_access,
          ai_capabilities: effectiveCaps,
          action_plan_access: effectiveActionPlanAccess,
        });
        toast.success('User updated');
      } else {
        if (!form.email.trim()) return toast.error('Email is required');
        if (!form.password || form.password.length < 8) return toast.error('Password must be at least 8 characters');
        await api.post('/admin/users', {
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          organization_id: form.organization_id,
          roles: form.roles,
          page_permissions: form.page_permissions,
          ai_access: form.ai_access,
          ai_capabilities: effectiveCaps,
          action_plan_access: effectiveActionPlanAccess,
        });
        toast.success('User created');
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success('User deleted');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? 'User disabled' : 'User enabled');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !resetPassword) return;
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPassword });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
      setResetPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
  };

  const handleResendInvite = async (rotate) => {
    if (!inviteTarget) return;
    if (rotate && (!inviteNewPassword || inviteNewPassword.length < 8)) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setInviteSending(true);
    try {
      const body = rotate ? { new_password: inviteNewPassword } : {};
      const res = await api.post(`/admin/users/${inviteTarget.id}/resend-invite`, body);
      if (res.data?.delivered) {
        toast.success(`Invite sent to ${inviteTarget.email}${rotate ? ' with a fresh password' : ''}`);
        setInviteTarget(null);
        setInviteNewPassword('');
      } else {
        toast.error('Send accepted but delivery not confirmed. Check Resend dashboard.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite send failed. Check server logs / Resend dashboard.');
    } finally {
      setInviteSending(false);
    }
  };

  const showAnalyticsPages = form.roles.includes('data_analytics');
  const sodError = checkSoD(form.roles);
  const seatsFull = isOrgAdmin && seats && !seats.unlimited && seats.remaining <= 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  // Filter role picker for Org Admins (server also enforces this)
  const allowedRoleKeys = isSuperAdmin
    ? Object.keys(ROLE_META)
    : (rolesConfig?.org_admin_assignable_roles || Object.keys(ROLE_META).filter(k => k !== 'super_admin' && k !== 'org_admin'));

  return (
    <div className="space-y-4" data-testid="user-management-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground mt-1">
            {users.length} user{users.length === 1 ? '' : 's'}
            {isSuperAdmin ? ` across ${orgs.length} organization(s)` : (seats ? ` — ${seats.used}/${seats.unlimited ? '∞' : seats.cap} seats used` : '')}
            {isOrgAdmin && seats && !seats.unlimited && seats.remaining > 0 && seats.remaining <= 2 && (
              <span className="ml-2 text-amber-600 dark:text-amber-400" data-testid="seats-warning">({seats.remaining} left)</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAudit} data-testid="audit-log-btn">
            <Clock className="w-4 h-4 mr-2" /> Audit Log
          </Button>
          <Button
            size="sm"
            onClick={openCreate}
            disabled={seatsFull}
            title={seatsFull ? 'Seat cap reached. Contact your platform administrator to upgrade the plan.' : undefined}
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create User
          </Button>
        </div>
      </div>

      {isOrgAdmin && seats && (
        <div className={`text-xs px-3 py-2 rounded-md border ${seatsFull ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800' : 'bg-muted/30 border-muted text-muted-foreground'}`} data-testid="seats-banner">
          Seats: <span className="font-semibold">{seats.used}</span> / {seats.unlimited ? '∞ (unlimited)' : seats.cap}
          {seatsFull ? ' — seat cap reached. Contact your platform administrator to upgrade the plan.' : ''}
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4 font-medium text-muted-foreground">User</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground">Roles</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-center">AI Access</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-center">Pages</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-center">Status</th>
                  <th className="py-2 font-medium text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <>{users.map(u => {
                  const targetRoles = u.roles || [];
                  const targetIsSuperOrOrgAdmin = targetRoles.includes('super_admin') || targetRoles.includes('org_admin');
                  const targetIsRoot = !!u.is_root;
                  const isSelfRow = me.id === u.id;
                  const orgAdminCanTouch = !(isOrgAdmin && targetIsSuperOrOrgAdmin);
                  // Root user is immutable — only the root user themselves can touch their own row.
                  const rootProtected = targetIsRoot && !isSelfRow;
                  const canTouch = orgAdminCanTouch && !rootProtected;
                  return (
                  <tr key={u.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors" data-testid="user-row">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium flex items-center gap-1.5">
                            {u.full_name}
                            {targetIsRoot && (
                              <span
                                title="Root Super Admin — env-controlled, cannot be modified"
                                className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/40 dark:text-amber-300"
                                data-testid="root-badge"
                              >
                                <Shield className="w-2.5 h-2.5" /> ROOT
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {targetRoles.map(r => (
                          <Badge key={r} variant="outline" className={`text-[10px] ${ROLE_META[r]?.color || ''}`}>
                            {ROLE_META[r]?.label || r}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-center">
                      {u.ai_access !== false ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <X className="w-4 h-4 text-muted-foreground mx-auto" />}
                    </td>
                    <td className="py-3 pr-4 text-center font-mono text-xs">
                      {targetRoles.includes('super_admin') ? 'All' : (u.page_permissions?.length || 0)}
                    </td>
                    <td className="py-3 pr-4 text-center">
                      <Badge variant={u.is_active !== false ? 'default' : 'secondary'} className="text-[10px]">
                        {u.is_active !== false ? 'Active' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canTouch && (
                          <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid="edit-user-btn">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canTouch && (
                          <Button variant="ghost" size="sm" onClick={() => { setResetTarget(u); setResetPassword(''); }} data-testid="reset-password-btn" title="Reset Password">
                            <KeyRound className="w-3.5 h-3.5 text-blue-500" />
                          </Button>
                        )}
                        {canTouch && (
                          <Button variant="ghost" size="sm" onClick={() => { setInviteTarget(u); setInviteNewPassword(''); }} data-testid="resend-invite-btn" title="Resend Invite Email">
                            <Send className="w-3.5 h-3.5 text-indigo-500" />
                          </Button>
                        )}
                        {canTouch && (
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u)} data-testid="toggle-user-btn">
                            {u.is_active !== false ? <X className="w-3.5 h-3.5 text-amber-500" /> : <Check className="w-3.5 h-3.5 text-emerald-500" />}
                          </Button>
                        )}
                        {!targetRoles.includes('super_admin') && canTouch && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(u)} data-testid="delete-user-btn">
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        )}
                        {rootProtected && (
                          <span className="text-[10px] text-muted-foreground italic px-2" title="Root account — controlled by ROOT_SUPER_ADMIN_* env vars">env-locked</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}</>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editUser ? 'Edit User' : 'Create User'}</CardTitle>
              <CardDescription>{editUser ? `Editing ${editUser.email}` : 'Add a new user to the system'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {!editUser && (
                <>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="user@company.com" data-testid="form-email" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label>Password</Label>
                      <div className="inline-flex rounded-md border border-border overflow-hidden text-[11px]">
                        <button
                          type="button"
                          onClick={() => changePasswordMode('auto')}
                          className={`px-2.5 py-1 transition-colors ${passwordMode === 'auto' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                          data-testid="pwd-mode-auto"
                        >
                          Generate
                        </button>
                        <button
                          type="button"
                          onClick={() => changePasswordMode('manual')}
                          className={`px-2.5 py-1 transition-colors ${passwordMode === 'manual' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                          data-testid="pwd-mode-manual"
                        >
                          Set manually
                        </button>
                      </div>
                    </div>
                    {passwordMode === 'auto' ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Input
                            type={showGeneratedPwd ? 'text' : 'password'}
                            readOnly
                            value={form.password}
                            className="font-mono text-sm bg-muted/40"
                            data-testid="form-password-generated"
                          />
                          <Button type="button" variant="ghost" size="sm" onClick={() => setShowGeneratedPwd(v => !v)} title={showGeneratedPwd ? 'Hide' : 'Show'} data-testid="pwd-toggle-visibility">
                            {showGeneratedPwd ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={copyGeneratedPassword} title="Copy to clipboard" data-testid="pwd-copy">
                            <Copy className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={regeneratePassword} title="Regenerate" data-testid="pwd-regenerate">
                            <RefreshCw className="w-4 h-4 text-emerald-500" />
                          </Button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          14-char cryptographically random password. Sent to the user in the invite email (if enabled) — copy it now if you plan to share it another way.
                        </p>
                      </div>
                    ) : (
                      <>
                        <Input
                          type="password"
                          value={form.password}
                          onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                          placeholder="Min 8 chars: uppercase, lowercase, digit, special"
                          autoComplete="new-password"
                          data-testid="form-password"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Server enforces min 8 chars with at least one uppercase, lowercase, digit, and special character.
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} data-testid="form-name" />
              </div>
              {!editUser && (
                <div className="space-y-2">
                  <Label>Organization</Label>
                  <Select value={form.organization_id} onValueChange={v => setForm(p => ({ ...p, organization_id: v }))}>
                    <SelectTrigger data-testid="form-org"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Roles */}
              <div className="space-y-2">
                <Label>Roles</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(ROLE_META).filter(([key]) => allowedRoleKeys.includes(key)).map(([key, meta]) => {
                    const active = form.roles.includes(key);
                    return (
                      <button key={key} type="button" onClick={() => toggleRole(key)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                        data-testid={`role-${key}`}>
                        <div className="flex items-center gap-2">
                          <meta.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{meta.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {sodError && (
                  <div className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
                    <AlertTriangle className="w-4 h-4 shrink-0" /> {sodError}
                  </div>
                )}
              </div>

              {/* Page Permissions (for data_analytics) */}
              {showAnalyticsPages && (
                <div className="space-y-2">
                  <Label>Analytics Page Access</Label>
                  <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
                    {Object.entries(PAGE_LABELS).map(([path, label]) => {
                      const active = form.page_permissions.includes(path);
                      return (
                        <button key={path} type="button" onClick={() => togglePage(path)}
                          className={`px-3 py-1.5 rounded text-xs text-left transition-all border ${active ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'border-border text-muted-foreground'}`}
                          data-testid={`page-${path.replace(/\//g, '-')}`}>
                          {active ? <Check className="w-3 h-3 inline mr-1" /> : null}{label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* AI Access — master toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="text-sm font-medium">AI Insights & Recommendations</p>
                  <p className="text-xs text-muted-foreground">Master switch: turn off to disable all AI features for this user</p>
                </div>
                <Switch checked={form.ai_access} onCheckedChange={v => setForm(p => ({ ...p, ai_access: v }))} data-testid="form-ai-toggle" />
              </div>

              {/* Granular AI Capabilities */}
              {form.ai_access && (
                <div className="rounded-lg border divide-y" data-testid="ai-capabilities-section">
                  <div className="px-3 py-2 bg-muted/30">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">AI Capabilities</p>
                    <p className="text-[11px] text-muted-foreground">
                      Silo specific AI features for this user (e.g. read insights but not generate action plans)
                      {isOrgAdmin ? '. Capabilities greyed below are not part of your org subscription — contact your platform administrator to enable.' : ''}
                    </p>
                  </div>
                  {AI_CAPABILITY_KEYS.map(cap => {
                    const meta = AI_CAPABILITY_LABELS[cap];
                    // For Org Admins, mirror the backend feature-ceiling logic
                    const capToFeature = { ai_recommendations: 'ai_recommendations', ai_action_plans: 'action_plan', ai_category_strategy: 'category_management' };
                    const featKey = capToFeature[cap];
                    const orgFeats = (orgs.find(o => o.id === form.organization_id) || {}).subscribed_features || [];
                    const featureLocked = isOrgAdmin && featKey && !orgFeats.includes(featKey);
                    const enabled = !featureLocked && (form.ai_capabilities?.[cap] !== false);
                    return (
                      <div key={cap} className={`flex items-center justify-between px-3 py-2.5 ${featureLocked ? 'opacity-60' : ''}`}>
                        <div className="min-w-0 pr-3">
                          <p className="text-sm flex items-center gap-1.5">
                            {meta.label}
                            {featureLocked && <span className="text-[10px] font-medium text-muted-foreground">(not subscribed)</span>}
                          </p>
                          <p className="text-[11px] text-muted-foreground">{meta.desc}</p>
                        </div>
                        <Switch
                          checked={enabled}
                          onCheckedChange={() => !featureLocked && toggleCapability(cap)}
                          disabled={featureLocked}
                          data-testid={`form-cap-${cap}`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleSave} disabled={saving || !!sodError} data-testid="form-save-btn">
                  {saving ? 'Saving...' : editUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Audit Log</CardTitle>
                <CardDescription>{auditLogs.length} entries</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAudit(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {auditLogs.map((log, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded border text-sm" data-testid="audit-entry">
                    <Badge variant="outline" className="shrink-0 text-[10px]">{log.action}</Badge>
                    <div className="flex-1 min-w-0">
                      <p><span className="font-medium">{log.actor_email}</span> {log.action.replace('_', ' ')} <span className="font-medium">{log.target_email || ''}</span></p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <pre className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{JSON.stringify(log.details, null, 2)}</pre>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                ))}
                {auditLogs.length === 0 && <p className="text-center text-muted-foreground py-6">No audit entries yet</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Resend Invite Modal */}
      {inviteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="resend-invite-modal">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Send className="w-4 h-4 text-indigo-500" /> Resend Invite Email
              </CardTitle>
              <CardDescription>
                Send a fresh welcome email to <span className="font-semibold">{inviteTarget.email}</span>.
                Optionally rotate their password first — recommended if the original invite may have leaked.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>New Temp Password <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  type="text"
                  value={inviteNewPassword}
                  onChange={e => setInviteNewPassword(e.target.value)}
                  placeholder="Leave blank to resend with existing password"
                  autoComplete="off"
                  data-testid="resend-invite-password-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Min 8 chars: uppercase, lowercase, digit, special.</p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setInviteTarget(null); setInviteNewPassword(''); }} disabled={inviteSending} data-testid="resend-invite-cancel">
                  Cancel
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleResendInvite(false)} disabled={inviteSending || !!inviteNewPassword} data-testid="resend-invite-send-only" title="Send the invite with the user's current password">
                  Send only
                </Button>
                <Button size="sm" onClick={() => handleResendInvite(true)} disabled={inviteSending || !inviteNewPassword} data-testid="resend-invite-rotate-and-send">
                  {inviteSending ? 'Sending…' : 'Rotate password + send'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="reset-password-modal">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> Reset Password
              </CardTitle>
              <CardDescription>Set a new password for <span className="font-semibold">{resetTarget.email}</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                  placeholder="Min 8 chars, uppercase, lowercase, digit, special"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setResetTarget(null); setResetPassword(''); }} data-testid="reset-password-cancel">
                  Cancel
                </Button>
                <Button size="sm" onClick={handleResetPassword} disabled={!resetPassword} data-testid="reset-password-confirm">
                  Reset Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
